import React from 'react';
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
} from '@mui/x-data-grid';
import { Paper, Typography, Box, Chip } from '@mui/material';
import { Wrestler } from '../hooks/useCsvImport';

interface WrestlersViewProps {
  wrestlers: Array<Wrestler & { schoolName: string }>;
}

const WrestlersView: React.FC<WrestlersViewProps> = ({ wrestlers }) => {
  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 150,
      editable: false,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 150,
      editable: false,
    },
    {
      field: 'schoolName',
      headerName: 'School',
      width: 200,
      editable: false,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          variant="outlined" 
          size="small" 
          color="primary" 
        />
      ),
    },
    {
      field: 'weightKg',
      headerName: 'Weight (kg)',
      width: 120,
      type: 'number',
      editable: false,
    },
    {
      field: 'age',
      headerName: 'Age',
      width: 100,
      type: 'number',
      editable: false,
      valueGetter: (params) => params.row.age || '-',
    },
    {
      field: 'grade',
      headerName: 'Grade',
      width: 100,
      editable: false,
      valueGetter: (params) => params.row.grade || '-',
    },
  ];

  const rows: GridRowsProp = wrestlers.map((wrestler) => ({
    id: wrestler.id,
    firstName: wrestler.firstName,
    lastName: wrestler.lastName,
    schoolName: wrestler.schoolName,
    weightKg: wrestler.weightKg,
    age: wrestler.age,
    grade: wrestler.grade,
  }));

  // Group wrestlers by school for summary
  const schoolCounts = wrestlers.reduce((acc, wrestler) => {
    acc[wrestler.schoolName] = (acc[wrestler.schoolName] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        All Submitted Wrestlers
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Master list of all wrestlers submitted to the tournament.
      </Typography>

      {/* School Summary */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Tournament Summary
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Total Wrestlers: ${wrestlers.length}`} color="primary" />
          <Chip label={`Total Schools: ${Object.keys(schoolCounts).length}`} color="secondary" />
          {Object.entries(schoolCounts).map(([school, count]) => (
            <Chip key={school} label={`${school}: ${count}`} variant="outlined" />
          ))}
        </Box>
      </Paper>

      {/* Wrestlers Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Wrestler Details ({wrestlers.length})
        </Typography>
        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
              sorting: {
                sortModel: [{ field: 'schoolName', sort: 'asc' }],
              },
            }}
            disableRowSelectionOnClick
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default WrestlersView;
